import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import {
	CustomTextInput,
	UserInfoTextInput,
} from "../../common-components/inputs";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { BlueButton } from "../../common-components/button";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { Severity } from "node-library/dist/constants";
import { getPasswordRequirements } from "../../helpers/passwordHelper";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";

interface Props {}

export default function ResetPassword(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const location = useLocation();
	const navigate = useNavigate();

	const hasValidated = useRef(false); // Track if validation has occurred
	const [userId, setUserId] = useState<string>();
	const [token, setToken] = useState<string>();
	const [validationFailed, setValidationFailed] = useState<boolean>(false);

	//for password
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");

	//validation
	const [passwordError, setPasswordError] = useState<string>("");
	const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(0);

	const passwordRequirements: Array<{
		satisfied: boolean | "";
		description: string;
	}> = getPasswordRequirements(password, translation);

	const [currentErrorColor, setCurrentErrorColor] = useState<string>(
		NeutralColor.NEUTRAL_GREY_300
	);

	const fieldsForPassword: Array<any> = [
		{
			id: "reset-password-input",
			label: `${translation("Password")}`,
			inputType: "password",
			value: password,
			setValue: setPassword,
			required: true,
			errorMsg: passwordError,
			setErrorMsg: setPasswordError,
			additinoalInfo: passwordRequirements.map((req, index) => {
				return (
					<div
						key={index}
						className="typography-caption"
						style={{
							color: req.satisfied ? SemanticColor.SUCCESS : currentErrorColor,
							marginLeft: "10px",
							display: "flex",
							gap: "5px",
							alignItems: "center",
							textAlign: "start",
						}}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							{req.satisfied ? (
								<CheckIcon sx={{ fontSize: 12 }} />
							) : (
								<CloseIcon sx={{ fontSize: 12 }} />
							)}
						</div>
						<div>{req.description}</div>
					</div>
				);
			}),
		},
		{
			id: "reset-confirm-password-input",
			label: `${translation("ConfirmPassword")}`,
			inputType: "password",
			value: confirmPassword,
			setValue: setConfirmPassword,
			required: true,
			errorMsg: confirmPasswordError,
			setErrorMsg: setConfirmPasswordError,
		},
	];

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const token = params.get("token");

		if (token && !hasValidated.current) {
			setToken(token);
			findUser(token);
		}
	}, [location]);

	const findUser = async (token: string) => {
		hasValidated.current = true;
		try {
			const response = await sendPOSTRequest("/logins/forget-password-entry", {
				token,
			});
			setUserId(response.data);
		} catch (error) {
			setValidationFailed(true);
		}
	};

	async function handleSubmit() {
		// Simple validation for required fields
		for (const field of fieldsForPassword) {
			if (field.required && !field.value) {
				field.setErrorMsg(translation("MissingInformation"));
				return;
			} else {
				field.setErrorMsg("");
			}
		}
		//password requirement
		for (const req of passwordRequirements) {
			if (!req.satisfied) {
				setCurrentErrorColor(SemanticColor.ERROR);
				return;
			}
		}

		//additional validations
		if (password !== confirmPassword) {
			setConfirmPasswordError(translation("PasswordDoesNotMatch"));
			return;
		}
		try {
			setLoading(true);
			const data = await sendPOSTRequest(
				"/logins/forget-password-reset-password",
				{
					userId,
					token,
					password,
				}
			);
			setCurrentPage(1);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	let pageContent: JSX.Element = <></>;
	if (userId) {
		const fields: Array<React.ReactNode> = fieldsForPassword.map(
			(field, index) => {
				return (
					<CustomTextInput
						id={field.id}
						key={field.id}
						value={field.value}
						setValue={field.setValue}
						placeholder={field.label}
						type="password"
						required={field.required}
						errorMsg={field.errorMsg}
						setErrorMsg={field.setErrorMsg}
						additinoalInfo={field.additinoalInfo}
					/>
				);
			}
		);
		switch (currentPage) {
			case 0:
				pageContent = (
					<>
						<div className="set-password-form">
							<div className="password-lock-icon"></div>
							<div
								className="typography-heading-01"
								style={{ marginTop: "10px" }}
							>
								{translation("ResetYourPassword")}
							</div>
							<div
								className="typography-body-02"
								style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
							>
								{translation("ResetYourPasswordDescription")}
							</div>
						</div>

						<div className="email-validation-password-form">
							{fields}
							<BlueButton
								id="set-password-button"
								children={
									loading ? (
										<ButtonTextLoadingAnimation
											text={translation("Loading")}
										></ButtonTextLoadingAnimation>
									) : (
										translation("SetPassword")
									)
								}
								size={"flex"}
								disabled={loading}
								onClick={() => {
									handleSubmit();
								}}
							></BlueButton>
						</div>
					</>
				);
				break;
			case 1:
				pageContent = (
					<>
						<div className="set-password-form">
							<div className="password-tick-icon"></div>
							<div className="typography-heading-01">
								{translation("PasswordUpdated")}
							</div>
							<div
								className="typography-body-02"
								style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
							>
								{translation("PasswordUpdatedDescription")}
							</div>
						</div>

						<div className="email-validation-password-form">
							<BlueButton
								id="set-password-button"
								children={
									loading ? (
										<ButtonTextLoadingAnimation
											text={translation("Loading")}
										></ButtonTextLoadingAnimation>
									) : (
										translation("Signin")
									)
								}
								size={"flex"}
								disabled={loading}
								onClick={() => {
									navigate("/login");
								}}
							></BlueButton>
						</div>
					</>
				);
				break;
		}
	} else if (validationFailed) {
		pageContent = (
			<div className="set-password-form">
				<div className="email-validation-error-icon "></div>
				<div className="typography-heading-01" style={{ marginTop: "20px" }}>
					{translation("ValidationLinkExpired")}
				</div>

				<div
					className="typography-body-02"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("ValidationLinkExpiredDescription")}
				</div>
			</div>
		);
	}

	return (
		<div className="email-validation-body">
			<div className="email-validation-content">
				<div>
					<div className="ppb-logo"></div>
					{pageContent}
				</div>
				<div className="typography-caption typography-bold login-footer">
					{translation("PoweredByBillionSpectra")}
				</div>
			</div>
		</div>
	);
}
